import {useState, useEffect, createContext, useContext} from 'react';
import jwtDecode from 'jwt-decode';

import config from "../../constants";
import http from "../../services/httpService";

const AuthContext = createContext();

export const AuthProvider = ({children, tenantId, wordpressToken = null}) => {
    const [tokens, setTokens] = useState({
        access: localStorage.getItem('access_token'),
        refresh: localStorage.getItem('refresh_token'),
    });
    const [user, setUser] = useState(() => {
        const storedUser = localStorage.getItem('user_data');
        return storedUser ? JSON.parse(storedUser) : null;
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // If WordPress token is provided, always authenticate with it
        if (wordpressToken) {
            try {
                const decodedWpToken = jwtDecode(wordpressToken);
                const currentWpUserId = decodedWpToken.user_id || decodedWpToken.sub;

                // Always authenticate with WordPress when token is provided
                // The Embed component handles cache clearing based on clear_cache parameter
                authenticateWithWordPress(wordpressToken, tenantId, currentWpUserId);
            } catch (err) {
                console.error('Failed to decode WordPress token:', err);
                // If we can't decode the token, authenticate anyway
                authenticateWithWordPress(wordpressToken, tenantId, null);
            }
        } else if (tokens.access && user) {
            // No WordPress token provided, use existing JWT if available
            setLoading(false);
        } else {
            setLoading(false);
        }
    }, [wordpressToken, tenantId]);

    const authenticateWithWordPress = async (wpToken, tenantId, wpUserId) => {
        try {
            const response = await fetch(`${config.API_URL}/wp/auth/wordpress/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Tenant-ID': tenantId
                },
                body: JSON.stringify({wordpress_token: wpToken, tenant_id: tenantId}),
            });
            if (response.ok) {
                const data = await response.json();
                updateTokens(data.access, data.refresh);
                setUser(data.user);
                localStorage.setItem('user_data', JSON.stringify(data.user));

                // Store the WordPress user ID for future validation
                if (wpUserId) {
                    localStorage.setItem('wp_user_id', String(wpUserId));
                } else if (data.user && data.user.wordpress_user_id) {
                    localStorage.setItem('wp_user_id', String(data.user.wordpress_user_id));
                }
            } else {
                console.error('WordPress authentication failed with status:', response.status);
                // If authentication fails, clear tokens
                logout();
            }
        } catch (err) {
            console.error('WordPress authentication failed:', err);
            logout();
        } finally {
            setLoading(false);
        }
    };

    const updateTokens = (access, refresh) => {
        setTokens({access, refresh});
        localStorage.setItem('access_token', access);
        localStorage.setItem('refresh_token', refresh);

        // CRITICAL: Update the axios instance's default Authorization header
        // This ensures all subsequent API calls use the new token
        http.setJwt(access);
    };

    const logout = () => {
        setTokens({access: null, refresh: null});
        setUser(null);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_data');
        localStorage.removeItem('wp_user_id');

        // Clear the axios instance's Authorization header
        http.removeJwt();
    };

    const refreshToken = async () => {
        try {
            const response = await fetch(`${config.API_URL}/wp/auth/refresh/`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({refresh: tokens.refresh}),
            });
            if (response.ok) {
                const data = await response.json();
                updateTokens(data.access, tokens.refresh);
                return data.access;
            } else {
                logout();
            }
        } catch (err) {
            logout();
        }
        return null;
    };

    return (
        <AuthContext.Provider value={{
            user,
            tokens,
            loading,
            logout,
            refreshToken,
            isAuthenticated: !!user,
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
